import { useEffect, useState } from "react";
import { api, type NotifChannelPref, type NotifPrefType, type NotifPrefs } from "../api";

/** The tunable notification types, with human labels for the settings rows. */
const ROWS: { type: NotifPrefType; label: string; desc: string }[] = [
  {
    type: "dispatch_failed",
    label: "Post failed",
    desc: "A scheduled post couldn't be published.",
  },
  {
    type: "reconnect_needed",
    label: "Reconnect needed",
    desc: "A connected account needs to be reconnected.",
  },
  {
    type: "credits_low",
    label: "Low credits",
    desc: "Your publish credits are running low.",
  },
];

// Everything defaults ON — a stored row only ever records a deviation from that.
const DEFAULT_CHANNELS: NotifChannelPref = { inApp: true, email: true };

/** The effective preference for one type: the stored value, or the default. */
function channelsFor(prefs: NotifPrefs, type: NotifPrefType): NotifChannelPref {
  return prefs[type] ?? DEFAULT_CHANNELS;
}

/**
 * Notifications section of the Control Center: per-type toggles for the in-app
 * inbox and email delivery. Both channels are live — an actionable notification
 * (a failed post, an account needing reconnection, low credits) is emailed
 * unless its email toggle is off. Saving always writes the full, defaults-filled
 * preference set so the server honours it consistently.
 */
export function NotificationPrefs() {
  const [prefs, setPrefs] = useState<NotifPrefs | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Marketing-email consent is tracked separately from the transactional
  // toggles: null until loaded, then the stored value (defaults false — never
  // pre-checked).
  const [marketing, setMarketing] = useState<boolean | null>(null);
  const [marketingSaving, setMarketingSaving] = useState(false);
  const [marketingError, setMarketingError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .preferences()
      .then((p) => {
        if (!cancelled) {
          setPrefs(p.notifPrefs);
          setMarketing(p.marketingOptIn);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Couldn't load your preferences.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Record an affirmative opt-in (or a withdrawal). Optimistic, with rollback.
  async function toggleMarketing(value: boolean) {
    const previous = marketing;
    setMarketing(value);
    setMarketingError(null);
    setMarketingSaving(true);
    try {
      const saved = await api.setMarketingConsent(value);
      setMarketing(saved);
    } catch (err) {
      setMarketing(previous);
      setMarketingError(err instanceof Error ? err.message : "Couldn't save your choice.");
    } finally {
      setMarketingSaving(false);
    }
  }

  async function toggle(type: NotifPrefType, channel: keyof NotifChannelPref, value: boolean) {
    if (!prefs) return;
    // Build the complete, defaults-filled set so the persisted row is unambiguous.
    const next: NotifPrefs = {};
    for (const row of ROWS) next[row.type] = { ...channelsFor(prefs, row.type) };
    next[type] = { ...channelsFor(prefs, type), [channel]: value };
    const previous = prefs;
    setPrefs(next); // optimistic
    setSaveError(null);
    try {
      const saved = await api.savePreferences(next);
      setPrefs(saved);
    } catch (err) {
      setPrefs(previous); // roll back on failure
      setSaveError(err instanceof Error ? err.message : "Couldn't save your preferences.");
    }
  }

  return (
    <div className="card notif-prefs">
      <h2>Notifications</h2>
      {loadError ? (
        <p className="error small">{loadError}</p>
      ) : !prefs ? (
        <p className="muted small">Loading…</p>
      ) : (
        <>
          <div className="notif-prefs-head">
            <span className="grow" />
            <span className="notif-col muted small">In-app</span>
            <span className="notif-col muted small">Email</span>
          </div>
          <ul className="notif-prefs-list">
            {ROWS.map((row) => {
              const channels = channelsFor(prefs, row.type);
              return (
                <li key={row.type} className="notif-pref-row">
                  <div className="grow">
                    <div className="notif-pref-label">{row.label}</div>
                    <div className="muted small">{row.desc}</div>
                  </div>
                  <span className="notif-col">
                    <input
                      type="checkbox"
                      aria-label={`In-app: ${row.label}`}
                      checked={channels.inApp}
                      onChange={(e) => void toggle(row.type, "inApp", e.target.checked)}
                    />
                  </span>
                  <span className="notif-col">
                    <input
                      type="checkbox"
                      aria-label={`Email: ${row.label}`}
                      checked={channels.email}
                      onChange={(e) => void toggle(row.type, "email", e.target.checked)}
                    />
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="muted small">
            Email is sent for actionable alerts only (a failed post, an account needing
            reconnection, low credits) to your account's email address.
          </p>
        </>
      )}
      {saveError && <p className="error small">{saveError}</p>}

      {/* Marketing consent — deliberately its own section, below the divider, and
          UNCHECKED by default. Opting in is an affirmative act (GDPR), and the
          copy makes clear it is separate from the account/security emails the
          user always receives (CAN-SPAM: honour opt-out; this is the opt-out). */}
      {marketing !== null && (
        <div className="marketing-consent">
          <h2>Product updates</h2>
          <label className="marketing-consent-row">
            <input
              type="checkbox"
              checked={marketing === true}
              disabled={marketingSaving}
              onChange={(e) => void toggleMarketing(e.target.checked)}
            />
            <span>
              <span className="marketing-consent-label">Email me product updates &amp; tips</span>
              <span className="muted small">
                Occasional emails about new features, tips, and announcements. This is separate
                from your account and security emails — you&apos;ll always get those. Unsubscribe
                anytime.
              </span>
            </span>
          </label>
          {marketingError && <p className="error small">{marketingError}</p>}
        </div>
      )}
    </div>
  );
}
